import { useState } from 'react';
import { Box, Button, SvgIcon } from '@mui/material';
import { palette } from 'theme/palette';
import { theme } from 'theme';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccountNumber, selectSelectedAccountTableItem } from 'selectors/accountSelector';
import { selectSelectedProduct } from 'selectors/tableSelector';
import { useNavigate } from 'react-router';
import { selectPartnerId } from 'selectors/partnerStatusSelector';
import { setSelectedProduct } from 'reducers/tableReducer';
import { setAccountSelectedProduct } from 'reducers/accountReducer';
import NewTab from 'assets/icons/NewTab.svg?react';
import { useRelaxedInterval } from 'components/hooks/useRelaxedInterval';
import { orderCaseDocuments } from 'services/product-service/orderCaseDocuments';
import { distributeCaseDocuments } from 'services/product-service/distributeCaseDocuments';
import { getCaseDetails } from 'services/product-service/getCaseDetails';
import { selectSelectedCaseDocuments } from 'selectors/caseSelector';
import { DocumentStatuses } from 'types';
import { CaseProposal, type Document } from 'pco-product-service-internal-consumer';
import { LoadingButton } from 'components/common/LoadingButton';
import { closeModal, openModal } from 'reducers/modalReducer';
import { selectUserFeatureToggles } from 'selectors/userFeatureToggleSelector';
import { setCase } from 'reducers/caseReducer';
import { PRODUCTS_AND_SERVICES } from 'services/constants';
import { videoIdentificationStyles } from 'views/VideoIdentification/VideoIdentificationFormActions';
import { RootState } from 'store';

const styles = {
  container: {
    width: '90%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  containerForTwo: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
};

type Props = {
  caseId?: string;
};

export const CaseButtonActions = ({ caseId = '' }: Props) => {
  const selectedAccountCaseId = useSelector(selectSelectedAccountTableItem);
  const navigate = useNavigate();
  const selectedPartnerCaseId = useSelector(selectSelectedProduct);
  const featureToggles = useSelector(selectUserFeatureToggles);
  const disabled = !(selectedAccountCaseId || selectedPartnerCaseId);
  const accountNumber = useSelector(selectAccountNumber);
  const partnerId = useSelector(selectPartnerId);
  const selectedDocuments = useSelector(selectSelectedCaseDocuments);
  const dispatch = useDispatch();
  const DEFAULT_DELAY = 3000;
  const [delay, setDelay] = useState<number | null>(null);
  const [pollingCount, setPollingCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const features = useSelector(selectUserFeatureToggles);
  const CLM_URL = useSelector((state: RootState) => state.appConfig.CLM_URL);

  const handleAddProposal = () => {
    const id = selectedAccountCaseId ? selectedAccountCaseId : selectedPartnerCaseId;
    const url = `msd/offerings?create-for-case=${id}`;
    window.parent.postMessage({ message: 'redirectMSD', value: url }, CLM_URL);
  };

  const handleCancel = () => {
    if (accountNumber) {
      navigate(`/accounts?accountNumber=${accountNumber}`);
      dispatch(setAccountSelectedProduct(undefined));
    } else if (partnerId) {
      navigate(`/partner?partnerNumber=${partnerId}`);
      dispatch(setSelectedProduct(undefined));
    }
  };

  const handleDistribute = () => {
    setIsLoading(true);
    // noinspection JSIgnoredPromiseFromCall
    renderDocumentsRequest();
  };

  const renderDocumentsRequest = async () => {
    try {
      await orderCaseDocuments(selectedDocuments);
      setDelay(DEFAULT_DELAY);
    } catch (error: unknown) {
      setIsLoading(false);
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when ordering case documents',
              userErrorMessage: `An error occurred when ordering case documents. Please try again later.`,
            },
          },
        }),
      );
    }
  };

  const distributeDocuments = async (documentIds: string[], caseIdentifier: string) => {
    try {
      const response = await distributeCaseDocuments(caseIdentifier, documentIds);
      if (response) {
        setIsLoading(false);
        window.parent.postMessage(
          {
            message: 'redirectDocumentTab_V2',
            value: { distributionRequestId: response.distributionRequestId, initiator: PRODUCTS_AND_SERVICES },
          },
          CLM_URL,
        );
      }
    } catch (error: unknown) {
      setIsLoading(false);
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when distributing documents',
              userErrorMessage: 'An error occurred when distributing documents. Please try again later.',
            },
          },
        }),
      );
    }
  };

  const startPollingAndDistribute = async () => {
    setPollingCount(pollingCount + 1);
    const caseDetails = await getCaseDetails(caseId);
    dispatch(setCase(caseDetails));
    const documentsWithReadyState = caseDetails.proposals
      ?.map((proposal: CaseProposal) => proposal.documents)
      .flat(1)
      .filter(
        (document: Document | undefined) =>
          selectedDocuments.includes(document?.id ?? '') && document?.status === DocumentStatuses.COMPLETED,
      );
    if (documentsWithReadyState?.length === selectedDocuments.length) {
      setDelay(null);
      // noinspection JSIgnoredPromiseFromCall
      await distributeDocuments(selectedDocuments, caseId);
    }
    if (pollingCount === 6) {
      setDelay(null);
      setIsLoading(false);
      dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            okHandler: () => {
              dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Service unavailable',
              userErrorMessage: 'This service is currently unavailable. Please try again later.',
            },
          },
        }),
      );
    }
  };

  useRelaxedInterval(startPollingAndDistribute, delay);

  return (
    <Box data-testid="case-button-actions" sx={videoIdentificationStyles.actions}>
      <Box sx={styles.container}>
        <Button
          id="cancel-case-overview-button"
          data-testid="cancel-case-overview-button"
          variant="outlined"
          onClick={handleCancel}
          style={{ color: palette.black }}
        >
          Cancel
        </Button>
        <Box sx={styles.containerForTwo}>
          <Box>
            <Button
              id="add-proposal-in-msd-button"
              data-testid="add-proposal-in-msd-button"
              color="primary"
              variant="contained"
              disabled={
                disabled ||
                (partnerId
                  ? !featureToggles.PARTNER.CreateMandateProposal
                  : !featureToggles.BUSINESS_RELATION.CreateMandateProposal)
              }
              onClick={handleAddProposal}
              style={{ marginRight: theme.spacing(2) }}
            >
              Add proposal in MSD
              <SvgIcon style={{ padding: '1px', marginTop: '4px', marginLeft: '4px' }}>
                <NewTab />
              </SvgIcon>
            </Button>
          </Box>
          <Box>
            <LoadingButton
              id="distribute-documents-button"
              data-testid="distribute-documents-button"
              color="primary"
              variant="contained"
              disabled={
                selectedDocuments.length === 0 ||
                (partnerId
                  ? !features.PARTNER.CreateMandateProposal || !features.PARTNER.ViewMandateProposal
                  : !features.BUSINESS_RELATION.CreateMandateProposal ||
                    !features.BUSINESS_RELATION.ViewMandateProposal)
              }
              onClick={handleDistribute}
              style={{ marginRight: 0 }}
              loading={isLoading}
            >
              Distribute
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
